import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Card, Button } from '../components';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import {
  Users,
  CheckCircle,
  AlertCircle,
  LogIn,
  DollarSign,
  ShieldCheck,
  Settings,
  Building2,
  GraduationCap,
  HeadphonesIcon,
  Send,
  Loader2,
  User,
  MessageCircle,
  MessageSquarePlus,
  Bug,
  Lightbulb,
  Palette,
  HelpCircle,
  MoreHorizontal,
  ChevronDown,
  Megaphone,
  Calendar,
  MapPin,
  Video,
  Plus,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { chatAPI, Message } from '../services/chatService';
import { useSocket } from '../hooks/useSocket';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

interface AdminUser {
  id: string;
  email: string;
  role: 'professional' | 'opdrachtgever' | 'admin' | 'employer_member' | 'medewerker' | 'opleider';
  first_name: string;
  last_name: string;
  phone?: string;
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
  last_login?: string;
  employer_under?: { bedrijfsnaam?: string; email?: string; name?: string } | null;
  employer_membership?: {
    role: 'admin' | 'manager' | 'viewer';
    toegangsniveau: 'global_admin' | 'vestigingsmanager' | 'afdelingschef';
    vestiging?: string | null;
    afdeling?: string | null;
  } | null;
}


// ─── Admin klantenservice chat component ─────────────────────────────────────

interface SupportConversation {
  id: string;
  onderwerp: string;
  professional: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    profile_photo_path?: string;
  } | null;
  last_message: string;
  last_message_at: string;
  unread_count: number;
}

const AdminKlantenserviceView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['admin-support-conversations'],
    queryFn: chatAPI.getAdminSupportConversations,
    refetchInterval: 30_000,
  });

  const { isLoading: isLoadingMessages } = useQuery({
    queryKey: ['admin-support-messages', selectedConvId],
    queryFn: () => chatAPI.getMessages(selectedConvId!),
    enabled: !!selectedConvId,
  });

  // Sync fetched messages into local state
  const fetchedMessages = useQuery({
    queryKey: ['admin-support-messages', selectedConvId],
    queryFn: () => chatAPI.getMessages(selectedConvId!),
    enabled: !!selectedConvId,
  }).data;

  useEffect(() => {
    if (fetchedMessages) setMessages(fetchedMessages);
  }, [fetchedMessages]);

  const handleNewMessage = useCallback((msg: Message) => {
    if (msg.conversation_id === selectedConvId) {
      setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
    }
    queryClient.invalidateQueries({ queryKey: ['admin-support-conversations'] });
  }, [selectedConvId, queryClient]);

  useSocket({
    conversationId: selectedConvId ?? undefined,
    onNewMessage: handleNewMessage,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMutation = useMutation({
    mutationFn: (content: string) => chatAPI.sendMessage(selectedConvId!, content),
    onSuccess: (msg) => {
      setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
      queryClient.invalidateQueries({ queryKey: ['admin-support-conversations'] });
    },
    onError: () => toast.error(t('admin.klantenservice.sendError')),
  });

  const handleSend = () => {
    const content = newMessage.trim();
    if (!content || !selectedConvId) return;
    setNewMessage('');
    sendMutation.mutate(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const selectedConv = (conversations as SupportConversation[]).find((c) => c.id === selectedConvId);

  return (
    <Card padding={false}>
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800 transition-colors">
          {t('admin.klantenservice.back')}
        </button>
        <HeadphonesIcon className="w-5 h-5 text-[#0B1F3A]" />
        <h3 className="text-lg font-semibold text-gray-900">{t('admin.klantenservice.title')}</h3>
        {(conversations as SupportConversation[]).some((c) => c.unread_count > 0) && (
          <span className="ml-auto px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">
            {(conversations as SupportConversation[]).reduce((sum, c) => sum + c.unread_count, 0)} {t('admin.klantenservice.newMessages')}
          </span>
        )}
      </div>

      <div className="flex flex-col md:flex-row h-auto md:h-[calc(100vh-280px)] md:min-h-[500px]">
        {/* Conversation list */}
        <div className="w-full md:w-72 md:flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-100 overflow-y-auto max-h-64 md:max-h-none">
          {isLoading && (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-400">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">{t('admin.klantenservice.loading')}</span>
            </div>
          )}

          {!isLoading && (conversations as SupportConversation[]).length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              {t('admin.klantenservice.noMessages')}
            </div>
          )}

          {(conversations as SupportConversation[]).map((conv) => (
            <button
              key={conv.id}
              onClick={() => setSelectedConvId(conv.id)}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                selectedConvId === conv.id ? 'bg-blue-50 border-l-2 border-l-[#0B1F3A]' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {conv.professional
                        ? `${conv.professional.first_name} ${conv.professional.last_name}`
                        : t('admin.klantenservice.unknown')}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="ml-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{conv.last_message || t('admin.klantenservice.noMessagesYet')}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {!selectedConvId && (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3">
              <HeadphonesIcon size={32} className="text-gray-300" />
              <p className="text-sm">{t('admin.klantenservice.selectConversation')}</p>
            </div>
          )}

          {selectedConvId && (
            <>
              {/* Conv header */}
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-sm font-semibold text-gray-800">
                  {selectedConv?.professional
                    ? `${selectedConv.professional.first_name} ${selectedConv.professional.last_name}`
                    : t('admin.klantenservice.selectConversation')}
                </p>
                {selectedConv?.professional && (
                  <p className="text-xs text-gray-400">{selectedConv.professional.email}</p>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-white">
                {isLoadingMessages && (
                  <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
                    <Loader2 size={14} className="animate-spin" />
                    <span className="text-sm">{t('admin.klantenservice.loadingMessages')}</span>
                  </div>
                )}

                {messages.map((msg) => {
                  const isOwn = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%]`}>
                        <div
                          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                            isOwn
                              ? 'bg-[#0B1F3A] text-white rounded-br-sm'
                              : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                          }`}
                        >
                          {msg.content}
                        </div>
                        <p className={`text-[10px] text-gray-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                          {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true, locale: nl })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="px-4 py-3 border-t border-gray-100 bg-white">
                <div className="flex items-end gap-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('admin.klantenservice.replyPlaceholder')}
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] max-h-32 overflow-y-auto"
                    style={{ minHeight: '42px' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sendMutation.isPending}
                    className="w-10 h-10 rounded-xl bg-[#0B1F3A] text-white flex items-center justify-center hover:bg-[#1A3459] transition-colors disabled:opacity-40 flex-shrink-0"
                  >
                    {sendMutation.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

// ─── Admin verbeterpunten (platform feedback) ────────────────────────────────

// icon typed as React.ElementType to be compatible with LucideIcon
const ADMIN_CATEGORIE_LABELS: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  bug:             { icon: Bug,            color: 'text-red-500',    bg: 'bg-red-50' },
  feature_request: { icon: Lightbulb,      color: 'text-yellow-500', bg: 'bg-yellow-50' },
  ux_verbetering:  { icon: Palette,        color: 'text-purple-500', bg: 'bg-purple-50' },
  vraag:           { icon: HelpCircle,     color: 'text-blue-500',   bg: 'bg-blue-50' },
  overig:          { icon: MoreHorizontal, color: 'text-gray-500',   bg: 'bg-gray-50' },
};

interface AdminFeedbackItem {
  id: number;
  user_name: string;
  email: string;
  categorie: string;
  prioriteit: string;
  titel: string;
  beschrijving: string;
  status: string;
  admin_reactie?: string;
  created_at: string;
}

const AdminVerbeterpuntenView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { t } = useTranslation('pages');
  const queryClientFb = useQueryClient();
  const [selected, setSelected] = useState<AdminFeedbackItem | null>(null);
  const [reactie, setReactie] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editStatus, setEditStatus] = useState('');

  const statusOptions = [
    { value: 'ingediend',      label: t('admin.verbeterpunten.statusIngediend'),      color: 'bg-blue-100 text-blue-700' },
    { value: 'in_behandeling', label: t('admin.verbeterpunten.statusInBehandeling'), color: 'bg-yellow-100 text-yellow-700' },
    { value: 'opgelost',       label: t('admin.verbeterpunten.statusOpgelost'),       color: 'bg-green-100 text-green-700' },
    { value: 'afgewezen',      label: t('admin.verbeterpunten.statusAfgewezen'),      color: 'bg-gray-100 text-gray-500' },
  ];

  const { data: items = [], isLoading } = useQuery<AdminFeedbackItem[]>({
    queryKey: ['admin-platform-feedback'],
    queryFn: async () => {
      const res = await api.get('/admin/platform-feedback');
      return res.data?.data ?? [];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, admin_reactie }: { id: number; status: string; admin_reactie?: string }) => {
      const res = await api.patch(`/admin/platform-feedback/${id}`, { status, admin_reactie });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('admin.verbeterpunten.savedSuccess'));
      queryClientFb.invalidateQueries({ queryKey: ['admin-platform-feedback'] });
      setSelected(null);
    },
    onError: () => toast.error(t('admin.verbeterpunten.saveError')),
  });

  const filtered = filterStatus ? items.filter((i) => i.status === filterStatus) : items;

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

  const openDetail = (item: AdminFeedbackItem) => {
    setSelected(item);
    setReactie(item.admin_reactie ?? '');
    setEditStatus(item.status);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">
          {t('admin.verbeterpunten.back')}
        </button>
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <MessageSquarePlus size={20} className="text-purple-600" />
          {t('admin.verbeterpunten.title')}
        </h2>
        <span className="ml-auto text-sm text-gray-500">
          {items.length} {items.length !== 1 ? t('admin.verbeterpunten.submissionsPlural') : t('admin.verbeterpunten.submissions')}
        </span>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
          >
            <option value="">{t('admin.verbeterpunten.allStatuses')}</option>
            {statusOptions.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-2.5 text-gray-400 pointer-events-none" />
        </div>
        <span className="text-sm text-gray-400">{filtered.length} {t('admin.verbeterpunten.results')}</span>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('admin.verbeterpunten.loading')}</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-400">
          {t('admin.verbeterpunten.noItems')}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => {
            const cat = ADMIN_CATEGORIE_LABELS[item.categorie];
            const CatIcon = cat?.icon ?? MoreHorizontal;
            const statusInfo = statusOptions.find((s) => s.value === item.status) ?? statusOptions[0];
            const priorityColor = item.prioriteit === 'hoog' ? 'text-red-600 font-semibold' : item.prioriteit === 'laag' ? 'text-gray-400' : 'text-yellow-600';
            return (
              <div
                key={item.id}
                onClick={() => openDetail(item)}
                className="rounded-xl border border-gray-200 bg-white p-4 cursor-pointer hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${cat?.bg ?? 'bg-gray-50'}`}>
                      <CatIcon size={14} className={cat?.color ?? 'text-gray-500'} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.titel}</p>
                      <p className="text-xs text-gray-400">{item.user_name} · {fmtDate(item.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-medium ${priorityColor}`}>{item.prioriteit}</span>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2 pl-9 line-clamp-2">{item.beschrijving}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail / edit modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-bold text-gray-900 text-base pr-4">{selected.titel}</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0">✕</button>
            </div>
            <div className="text-xs text-gray-500 flex gap-3 flex-wrap">
              <span>{selected.user_name}</span>
              <span>{selected.email}</span>
              <span>{fmtDate(selected.created_at)}</span>
            </div>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selected.beschrijving}</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('admin.verbeterpunten.statusLabel')}</label>
              <div className="relative">
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                >
                  {statusOptions.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-2.5 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('admin.verbeterpunten.responseLabel')}</label>
              <textarea
                value={reactie}
                onChange={(e) => setReactie(e.target.value)}
                rows={3}
                placeholder={t('admin.verbeterpunten.responsePlaceholder')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                {t('admin.verbeterpunten.cancel')}
              </button>
              <button
                disabled={updateMutation.isPending}
                onClick={() => updateMutation.mutate({ id: selected.id, status: editStatus, admin_reactie: reactie || undefined })}
                className="px-5 py-2 rounded-lg bg-[#0B1F3A] text-white text-sm font-semibold hover:bg-[#1A3459] disabled:opacity-60 transition-colors"
              >
                {updateMutation.isPending ? t('admin.verbeterpunten.saving') : t('admin.verbeterpunten.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const AdminEventsView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({
    title: '',
    description: '',
    location: '',
    location_url: '',
    event_date: '',
    event_end_date: '',
    online: false,
    meeting_url: '',
    max_attendees: '',
  });

  const { data: events, isLoading } = useQuery({
    queryKey: ['admin-community-events'],
    queryFn: async () => {
      const res = await api.get('/community/events');
      return res.data.data as any[];
    },
  });

  const createMutation = useMutation({
    mutationFn: () => api.post('/community/events', {
      ...form,
      max_attendees: form.max_attendees ? parseInt(form.max_attendees) : undefined,
    }),
    onSuccess: () => {
      toast.success('Evenement aangemaakt');
      queryClient.invalidateQueries({ queryKey: ['admin-community-events'] });
      setShowForm(false);
      setForm({ title: '', description: '', location: '', location_url: '', event_date: '', event_end_date: '', online: false, meeting_url: '', max_attendees: '' });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Fout bij aanmaken'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/community/events/${id}`),
    onSuccess: () => {
      toast.success('Evenement verwijderd');
      queryClient.invalidateQueries({ queryKey: ['admin-community-events'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm">← Terug</button>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-600" />
            Community evenementen
          </h2>
        </div>
        <Button onClick={() => setShowForm(v => !v)} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="w-4 h-4 mr-1" />
          Nieuw evenement
        </Button>
      </div>

      {showForm && (
        <Card>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Nieuw evenement aanmaken</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="Naam van het evenement" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Datum & tijd start *</label>
              <input type="datetime-local" value={form.event_date} min={new Date().toISOString().slice(0, 16)} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Datum & tijd einde</label>
              <input type="datetime-local" value={form.event_end_date} min={form.event_date || undefined} onChange={e => setForm(f => ({ ...f, event_end_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Locatie</label>
              <input type="text" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="Adres of plaatsnaam" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max. deelnemers</label>
              <input type="number" value={form.max_attendees} onChange={e => setForm(f => ({ ...f, max_attendees: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="Leeg = onbeperkt" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="online" checked={form.online} onChange={e => setForm(f => ({ ...f, online: e.target.checked }))} className="rounded" />
              <label htmlFor="online" className="text-sm text-gray-700">Online evenement</label>
            </div>
            {form.online && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting URL</label>
                <input type="url" value={form.meeting_url} onChange={e => setForm(f => ({ ...f, meeting_url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="https://..." />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowForm(false)}>Annuleren</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!form.title || !form.event_date || createMutation.isPending} className="bg-teal-600 hover:bg-teal-700">
              {createMutation.isPending ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Laden...</div>
      ) : !events?.length ? (
        <Card><p className="text-center text-gray-500 py-6">Nog geen evenementen gepland.</p></Card>
      ) : (
        <div className="space-y-3">
          {events.map((ev: any) => (
            <Card key={ev.id}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{ev.title}</h3>
                    {ev.online && <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Video className="w-3 h-3" /> Online</span>}
                    {new Date(ev.event_date) < new Date() && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Verlopen</span>}
                  </div>
                  {ev.description && <p className="text-sm text-gray-600 mb-2">{ev.description}</p>}
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(ev.event_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                    <span>{ev.attendee_count || 0} aangemeld{ev.max_attendees ? ` / ${ev.max_attendees} max` : ''}</span>
                  </div>
                </div>
                <button onClick={() => { if (confirm('Evenement verwijderen?')) deleteMutation.mutate(ev.id); }}
                  className="ml-4 text-red-400 hover:text-red-600 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const AdminBroadcastView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [target, setTarget] = React.useState<'all' | 'professionals' | 'employers'>('all');
  const [result, setResult] = React.useState<{ sent: number; failed: number; total: number } | null>(null);

  const broadcastMutation = useMutation({
    mutationFn: () => api.post('/admin/broadcast', { subject, message, target }),
    onSuccess: (res: any) => {
      const data = res.data;
      if (data.success) {
        setResult(data.data);
        setSubject('');
        setMessage('');
      }
    },
  });

  const TARGET_LABELS = { all: 'Alle gebruikers', professionals: 'Alleen professionals', employers: 'Alleen opdrachtgevers' };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm">
          ← Terug
        </button>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-red-600" />
          Broadcast bericht versturen
        </h2>
      </div>

      <Card>
        <div className="space-y-5">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Dit stuurt een e-mail naar alle geselecteerde actieve gebruikers. Gebruik dit spaarzaam.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Doelgroep</label>
            <div className="flex gap-3">
              {(['all', 'professionals', 'employers'] as const).map(t => (
                <label key={t} className="flex items-center cursor-pointer">
                  <input type="radio" name="target" value={t} checked={target === t} onChange={() => setTarget(t)} className="mr-2" />
                  <span className="text-sm">{TARGET_LABELS[t]}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Onderwerp</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Onderwerp van de e-mail"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bericht</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={8}
              placeholder="Schrijf hier uw bericht..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm resize-none"
            />
          </div>

          {result && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-800">Bericht verstuurd!</p>
              <p className="text-sm text-green-700 mt-1">
                {result.sent} van {result.total} berichten succesvol verstuurd
                {result.failed > 0 && `, ${result.failed} mislukt`}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onBack}>Annuleren</Button>
            <Button
              onClick={() => broadcastMutation.mutate()}
              disabled={!subject || !message || broadcastMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              <Send className="w-4 h-4 mr-2" />
              {broadcastMutation.isPending ? 'Versturen...' : 'Verstuur broadcast'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get('view') === 'users' ? 'users' : 'dashboard';
  const [activeView, setActiveView] = useState<'dashboard' | 'users' | 'screenings' | 'klantenservice' | 'verbeterpunten' | 'broadcast' | 'events'>(initialView);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVerified, setFilterVerified] = useState<'all' | 'verified' | 'unverified'>('all');
  const [filterRole, setFilterRole] = useState<'all' | 'professional' | 'opdrachtgever' | 'admin' | 'employer_member' | 'medewerker' | 'opleider'>('all');
  const [filterMemberRole, setFilterMemberRole] = useState<'all' | 'admin' | 'manager' | 'viewer'>('all');
  const [filterNiveau, setFilterNiveau] = useState<'all' | 'global_admin' | 'vestigingsmanager' | 'afdelingschef'>('all');

  // Update activeView when URL parameter changes
  useEffect(() => {
    const view = searchParams.get('view');
    if (view === 'users') {
      setActiveView('users');
    } else if (view === null || view === 'dashboard') {
      setActiveView('dashboard');
    }
  }, [searchParams]);

  // Fetch users
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await api.get('/admin/users');
      return response.data.data as AdminUser[];
    },
  });


  // Fetch platform stats
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/stats');
      return response.data.data;
    },
  });

  // Fetch screenings
  const { data: screenings } = useQuery({
    queryKey: ['admin-screenings'],
    queryFn: async () => {
      const response = await api.get('/admin/screening/screenings?status=pending');
      return response.data.data;
    },
  });

  // Login as user mutation
  const loginAsMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.post(`/admin/login-as/${userId}`);
      return response.data;
    },
    onSuccess: (data) => {
      // Store original admin session and switch to impersonated user
      const currentUser = useAuthStore.getState().user;
      const currentToken = useAuthStore.getState().token;

      if (currentUser && currentToken) {
        useAuthStore.getState().adminImpersonation(
          data.data.user,
          data.data.token,
          currentUser,
          currentToken
        );
        toast.success(data.message);
        // Redirect to dashboard of the user we're impersonating
        navigate('/dashboard');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('admin.dashboard.errorImpersonate'));
    },
  });

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card>
          <p className="text-sm text-gray-700">{t('admin.dashboard.adminOnly')}</p>
        </Card>
      </div>
    );
  }

  const handleLoginAs = (userId: string, userName: string) => {
    if (window.confirm(t('admin.dashboard.confirmLoginAs', { name: userName }))) {
      loginAsMutation.mutate(userId);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'opdrachtgever':
        return 'bg-blue-100 text-blue-800';
      case 'professional':
        return 'bg-green-100 text-green-800';
      case 'employer_member':
        return 'bg-amber-100 text-amber-800';
      case 'opleider':
        return 'bg-teal-100 text-teal-800';
      case 'medewerker':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    const roleKeys: Record<string, string> = {
      professional: 'admin.dashboard.professional',
      opdrachtgever: 'admin.dashboard.opdrachtgever',
      employer_member: 'admin.dashboard.employerMember',
      medewerker: 'admin.dashboard.medewerker',
      opleider: 'admin.dashboard.opleider',
      admin: 'admin.dashboard.admin',
    };
    return roleKeys[role] ? t(roleKeys[role]) : role;
  };

  // Sub-filters op rol en toegangsniveau gelden alleen binnen 'Werknemer opdrachtgever'
  const matchesMembership = (user: AdminUser) => {
    if (filterRole !== 'employer_member') return true;
    const m = user.employer_membership;
    return (filterMemberRole === 'all' || m?.role === filterMemberRole) &&
      (filterNiveau === 'all' || m?.toegangsniveau === filterNiveau);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Platform Statistics - Clickable Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <button
            onClick={() => setActiveView('users')}
            className="text-left transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
          >
            <Card padding={false} className="h-full hover:shadow-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('admin.dashboard.totalUsers')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.users?.total || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-2 text-xs text-blue-600 font-medium">
                  {t('admin.dashboard.clickUserManagement')}
                </div>
              </div>
            </Card>
          </button>


          <button
            onClick={() => setActiveView('users')}
            className="text-left transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-lg"
          >
            <Card padding={false} className="h-full hover:shadow-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('admin.dashboard.verifiedUsers')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.users?.verified || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-2 text-xs text-purple-600 font-medium">
                  {t('admin.dashboard.clickUserManagement')}
                </div>
              </div>
            </Card>
          </button>

          <button
            onClick={() => navigate('/admin/screenings')}
            className="text-left transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 rounded-lg"
          >
            <Card padding={false} className="h-full hover:shadow-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('admin.dashboard.pendingScreenings')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {screenings?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-2 text-xs text-yellow-600 font-medium">
                  {t('admin.dashboard.clickScreeningManagement')}
                </div>
              </div>
            </Card>
          </button>
        </div>
      )}

      {/* Quick Links Section */}
      {activeView === 'dashboard' && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.dashboard.quickLinks')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/admin/screenings')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <ShieldCheck className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.screenings')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.screeningsDesc')}</p>
            </button>

            <button
              onClick={() => navigate('/admin/invoices')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.invoices')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.invoicesDesc')}</p>
            </button>

            <button
              onClick={() => navigate('/admin/stripe-incidenten')}
              className="text-left p-4 bg-red-50 border border-red-200 rounded-lg hover:border-red-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.stripeIncidents')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.stripeIncidentsDesc')}</p>
            </button>

            <button
              onClick={() => navigate('/vacatures')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <Users className="w-5 h-5 text-purple-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.vacancies')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.vacanciesDesc')}</p>
            </button>

            <button
              onClick={() => navigate('/profiel')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <Settings className="w-5 h-5 text-gray-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.profile')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.profileDesc')}</p>
            </button>

            <button
              onClick={() => setActiveView('klantenservice')}
              className="text-left p-4 bg-blue-50 border border-blue-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <HeadphonesIcon className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.customerServiceChat')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.customerServiceChatDesc')}</p>
            </button>

            <button
              onClick={() => navigate('/admin/belastingdienst')}
              className="text-left p-4 bg-orange-50 border border-orange-200 rounded-lg hover:border-orange-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <Building2 className="w-5 h-5 text-orange-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.belastingdienstPortal')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.belastingdienstPortalDesc')}</p>
            </button>

            <button
              onClick={() => navigate('/admin/sbi-codes')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <Building2 className="w-5 h-5 text-teal-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.sbiCodes', { defaultValue: 'SBI-codes KVK-screening' })}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.sbiCodesDesc', { defaultValue: 'Geaccepteerde SBI-codes beheren' })}</p>
            </button>

            <button
              onClick={() => navigate('/admin/academy-subcategorieen')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <GraduationCap className="w-5 h-5 text-teal-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.academySubcategories', { defaultValue: 'Academy-subcategorieën' })}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.academySubcategoriesDesc', { defaultValue: 'Catalogus-pills per hoofdgroep beheren' })}</p>
            </button>

            <button
              onClick={() => navigate('/admin/academy-audit')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <GraduationCap className="w-5 h-5 text-teal-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.academyAudit', { defaultValue: 'Cursus-audit (compliance)' })}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.academyAuditDesc', { defaultValue: 'Ingediende cursussen keuren; uitslag landt in de opleidersfeed' })}</p>
            </button>

            <button
              onClick={() => navigate('/admin/kwaliteitspaspoorten')}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <ShieldCheck className="w-5 h-5 text-teal-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.qualityPassports', { defaultValue: 'Kwaliteitspaspoorten' })}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.qualityPassportsDesc', { defaultValue: 'Uitgegeven paspoorten bekijken en zo nodig intrekken' })}</p>
            </button>

            <button
              onClick={() => setActiveView('verbeterpunten')}
              className="text-left p-4 bg-purple-50 border border-purple-200 rounded-lg hover:border-purple-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <MessageSquarePlus className="w-5 h-5 text-purple-600 mr-2" />
                <span className="font-medium text-gray-900">{t('admin.dashboard.improvementPoints')}</span>
              </div>
              <p className="text-sm text-gray-500">{t('admin.dashboard.improvementPointsDesc')}</p>
            </button>

            <button
              onClick={() => setActiveView('broadcast')}
              className="text-left p-4 bg-red-50 border border-red-200 rounded-lg hover:border-red-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <Megaphone className="w-5 h-5 text-red-600 mr-2" />
                <span className="font-medium text-gray-900">Broadcast bericht</span>
              </div>
              <p className="text-sm text-gray-500">Stuur een bericht naar alle of specifieke gebruikers</p>
            </button>

            <button
              onClick={() => setActiveView('events')}
              className="text-left p-4 bg-teal-50 border border-teal-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all"
            >
              <div className="flex items-center mb-2">
                <Calendar className="w-5 h-5 text-teal-600 mr-2" />
                <span className="font-medium text-gray-900">Community evenementen</span>
              </div>
              <p className="text-sm text-gray-500">Aanmaken en beheren van evenementen voor de community</p>
            </button>
          </div>
        </div>
      )}


      {/* Dashboard Overview */}
      {activeView === 'dashboard' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('admin.dashboard.platformOverview')}</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('admin.dashboard.totalUsersLabel')}</span>
                <span className="text-sm font-medium">{stats?.users?.total || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('admin.dashboard.verifiedUsersLabel')}</span>
                <span className="text-sm font-medium">{stats?.users?.verified || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('admin.dashboard.activeUsersLabel')}</span>
                <span className="text-sm font-medium">{stats?.users?.active || 0}</span>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('admin.dashboard.quickLinks2')}</h3>
            <div className="space-y-3">
              <button
                onClick={() => setActiveView('users')}
                className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="font-medium">{t('admin.dashboard.userManagement')}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{t('admin.dashboard.userManagementDesc')}</p>
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Users Management View */}
      {activeView === 'users' && (
        <Card>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-2">
            <h3 className="text-lg font-medium text-gray-900">{t('admin.dashboard.userManagement')}</h3>
            <span className="text-sm text-gray-500">
              {users?.filter(user => {
                const matchesSearch = !searchQuery ||
                  `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  user.email.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesVerified = filterVerified === 'all' ||
                  (filterVerified === 'verified' && user.is_verified) ||
                  (filterVerified === 'unverified' && !user.is_verified);
                const matchesRole = filterRole === 'all' || user.role === filterRole;
                return matchesSearch && matchesVerified && matchesRole && matchesMembership(user);
              }).length || 0} {t('admin.dashboard.users')}
            </span>
          </div>

          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            {/* Search Bar */}
            <div>
              <label htmlFor="search-users" className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.dashboard.search')}
              </label>
              <input
                id="search-users"
                type="text"
                placeholder={t('admin.dashboard.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Verification Filter */}
              <div>
                <label htmlFor="filter-verified" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.dashboard.verificationStatus')}
                </label>
                <select
                  id="filter-verified"
                  value={filterVerified}
                  onChange={(e) => setFilterVerified(e.target.value as 'all' | 'verified' | 'unverified')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">{t('admin.dashboard.allUsers')}</option>
                  <option value="verified">{t('admin.dashboard.verified')}</option>
                  <option value="unverified">{t('admin.dashboard.notVerified')}</option>
                </select>
              </div>

              {/* Role Filter */}
              <div>
                <label htmlFor="filter-role" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.dashboard.role')}
                </label>
                <select
                  id="filter-role"
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value as 'all' | 'professional' | 'opdrachtgever' | 'admin' | 'employer_member' | 'medewerker' | 'opleider')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">{t('admin.dashboard.allRoles')}</option>
                  <option value="professional">{t('admin.dashboard.professional')}</option>
                  <option value="opdrachtgever">{t('admin.dashboard.opdrachtgever')}</option>
                  <option value="employer_member">{t('admin.dashboard.employerMember')}</option>
                  <option value="medewerker">{t('admin.dashboard.medewerker')}</option>
                  <option value="opleider">{t('admin.dashboard.opleider')}</option>
                  <option value="admin">{t('admin.dashboard.admin')}</option>
                </select>
              </div>

              {filterRole === 'employer_member' && (
                <>
                  <div>
                    <label htmlFor="filter-member-role" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('admin.dashboard.memberRole')}
                    </label>
                    <select
                      id="filter-member-role"
                      value={filterMemberRole}
                      onChange={(e) => setFilterMemberRole(e.target.value as 'all' | 'admin' | 'manager' | 'viewer')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">{t('admin.dashboard.allRoles')}</option>
                      <option value="admin">{t('admin.dashboard.memberRole_admin')}</option>
                      <option value="manager">{t('admin.dashboard.memberRole_manager')}</option>
                      <option value="viewer">{t('admin.dashboard.memberRole_viewer')}</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="filter-niveau" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('admin.dashboard.toegangsniveau')}
                    </label>
                    <select
                      id="filter-niveau"
                      value={filterNiveau}
                      onChange={(e) => setFilterNiveau(e.target.value as 'all' | 'global_admin' | 'vestigingsmanager' | 'afdelingschef')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">{t('admin.dashboard.allNiveaus')}</option>
                      <option value="global_admin">{t('admin.dashboard.niveau_global_admin')}</option>
                      <option value="vestigingsmanager">{t('admin.dashboard.niveau_vestigingsmanager')}</option>
                      <option value="afdelingschef">{t('admin.dashboard.niveau_afdelingschef')}</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {usersLoading ? (
            <div className="text-center py-6">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : users && users.length > 0 ? (
            <div className="space-y-3">
              {users
                .filter(user => {
                  const matchesSearch = !searchQuery ||
                    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    user.email.toLowerCase().includes(searchQuery.toLowerCase());
                  const matchesVerified = filterVerified === 'all' ||
                    (filterVerified === 'verified' && user.is_verified) ||
                    (filterVerified === 'unverified' && !user.is_verified);
                  const matchesRole = filterRole === 'all' || user.role === filterRole;
                  return matchesSearch && matchesVerified && matchesRole && matchesMembership(user);
                })
                .map((user) => (
                <div key={user.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0 w-full sm:w-auto">
                    <div className="flex items-start sm:items-center space-x-3">
                      <BelagomProfilePhoto
                        src={(user as any).photo_url}
                        alt={`${user.first_name} ${user.last_name}`}
                        size={56}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        <p className="text-xs text-gray-400">
                          {t('admin.dashboard.registeredAt')} {formatDate(user.created_at)}
                        </p>
                        {(user.role === 'employer_member' || user.role === 'medewerker') && user.employer_under && (
                          <p className="text-xs text-gray-600 mt-1">
                            {t('admin.dashboard.underEmployer')} {user.employer_under.bedrijfsnaam || user.employer_under.name || user.employer_under.email || '—'}
                          </p>
                        )}
                        {user.employer_membership && (
                          <p className="text-xs text-gray-600">
                            {t('admin.dashboard.scope')}{' '}
                            {[user.employer_membership.vestiging, user.employer_membership.afdeling].filter(Boolean).join(' · ') || t('admin.dashboard.scopeAll')}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col space-y-1 ml-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                        {user.employer_membership && (
                          <>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-900 border border-amber-200">
                              {t(`admin.dashboard.memberRole_${user.employer_membership.role}`)}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-900 border border-amber-200">
                              {t(`admin.dashboard.niveau_${user.employer_membership.toegangsniveau}`)}
                            </span>
                          </>
                        )}
                        {!user.is_verified && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {t('admin.dashboard.notVerifiedBadge')}
                          </span>
                        )}
                        {!user.is_active && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {t('admin.dashboard.inactiveBadge')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 sm:mt-0 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleLoginAs(user.id, `${user.first_name} ${user.last_name}`)}
                      loading={loginAsMutation.isPending}
                    >
                      <LogIn className="w-3 h-3 mr-1" />
                      {t('admin.dashboard.loginAs')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-6">{t('admin.dashboard.noUsersFound')}</p>
          )}
        </Card>
      )}

      {/* Klantenservice Chat View */}
      {activeView === 'klantenservice' && (
        <AdminKlantenserviceView onBack={() => setActiveView('dashboard')} />
      )}

      {/* Verbeterpunten Platform View */}
      {activeView === 'verbeterpunten' && (
        <AdminVerbeterpuntenView onBack={() => setActiveView('dashboard')} />
      )}

      {/* Broadcast View */}
      {activeView === 'broadcast' && (
        <AdminBroadcastView onBack={() => setActiveView('dashboard')} />
      )}

      {/* Events View */}
      {activeView === 'events' && (
        <AdminEventsView onBack={() => setActiveView('dashboard')} />
      )}

    </div>
  );
};
